import React, { useState } from "react";
import {
  Drawer,
  Spin,
  Row,
  Col,
  Tag,
  Alert,
  Button,
  Divider,
  Modal,
} from "antd";
import { useSelector } from "react-redux";
import useStockMovementContext from "../../../../hooks/useStockMovementContext";
import ButtonStandard from "../../../../components/utils/button";
import DanfeViewerModal from "../../../../components/utils/invoiceUploadViewer/DanfeViewerModal";
import {
  TYPE_TAG_COLOR,
  STATUS_TAG_COLOR,
  toI18nKey,
} from "./stockMovementConstants";
import { numberMask } from "../../../../services/helpersMethodsService";
import { momentTimeZoneHour } from "../../../../utils/dateUtils";

const Field = ({ label, value }) => (
  <Col span={12} style={{ marginBottom: 8 }}>
    <div style={{ fontSize: 12, color: "#888" }}>{label}</div>
    <div style={{ fontWeight: 500 }}>{value || "-"}</div>
  </Col>
);

const StockMovementDetailDrawer = () => {
  const { translation } = useSelector((state) => state.app);
  const tm = translation?.stockItem?.movements || {};
  const t = tm.detail || {};
  const tc = tm.deleteConfirm || {};

  const {
    isDrawerVisible,
    isLoadingEdit,
    dataMovement,
    groupId,
    farmId,
    handleCloseDrawer,
    handleRequestDelete,
  } = useStockMovementContext();

  const [danfeVisible, setDanfeVisible] = useState(false);

  const movement = dataMovement;
  const isBlocked = movement?.status === "AUDIT";

  const label = (group, value) =>
    (tm[group] && tm[group][toI18nKey(value)]) || value || "-";

  const confirmDelete = () => {
    Modal.confirm({
      title: tc.title || "Solicitar Exclusão de Movimento",
      content:
        tc.description ||
        "Um registro de auditoria será criado e a exclusão precisará ser aprovada por um segundo usuário.",
      okText: tc.confirm || "Confirmar Exclusão",
      okType: "danger",
      cancelText: translation?.buttons?.cancel || "Cancelar",
      onOk: () => handleRequestDelete(movement.id, movement.creationUser),
    });
  };

  const origin =
    movement?.type === "ENTRY"
      ? label("entryOrigins", movement?.origin)
      : label("exitOrigins", movement?.origin);

  return (
    <Drawer
      title={t.title || "Detalhes do Movimento"}
      visible={isDrawerVisible}
      placement="right"
      width={520}
      onClose={handleCloseDrawer}
    >
      <Spin spinning={isLoadingEdit}>
        {movement && (
          <>
            {isBlocked && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message={
                  t.blocked ||
                  "Movimento bloqueado — Existe uma solicitação de exclusão pendente de auditoria."
                }
              />
            )}

            <Divider orientation="left">
              {t.generalInfo || "Informações Gerais"}
            </Divider>
            <Row type="flex">
              <Field
                label={tm.table?.type || "Tipo"}
                value={
                  <Tag color={TYPE_TAG_COLOR[movement.type]}>
                    {label("type", movement.type)}
                  </Tag>
                }
              />
              <Field label={tm.table?.origin || "Origem"} value={origin} />
              <Field
                label={tm.table?.status || "Status"}
                value={
                  <Tag color={STATUS_TAG_COLOR[movement.status]}>
                    {label("status", movement.status)}
                  </Tag>
                }
              />
              <Field
                label={tm.table?.createdAt || "Data Criação"}
                value={
                  movement.createdAt
                    ? momentTimeZoneHour(movement.createdAt).format(
                        "DD/MM/YYYY HH:mm",
                      )
                    : "-"
                }
              />
              <Field
                label={tm.table?.user || "Usuário"}
                value={movement.userName}
              />
              {movement.requester && (
                <Field
                  label={tm.form?.requester || "Solicitante"}
                  value={movement.requester}
                />
              )}
              {movement.issueDate && (
                <Field
                  label={tm.form?.issueDate || "Data Retirada"}
                  value={momentTimeZoneHour(movement.issueDate).format(
                    "DD/MM/YYYY",
                  )}
                />
              )}
              {movement.productionDate && (
                <Field
                  label={tm.form?.productionDate || "Data de Produção"}
                  value={momentTimeZoneHour(movement.productionDate).format(
                    "DD/MM/YYYY",
                  )}
                />
              )}
              {movement.requestDate && (
                <Field
                  label="Data Solicitação"
                  value={momentTimeZoneHour(movement.requestDate).format(
                    "DD/MM/YYYY",
                  )}
                />
              )}
              {movement.nfReference && (
                <Field
                  label={tm.form?.nfReference || "Referência NF"}
                  value={movement.nfReference}
                />
              )}
              {movement.nfDate && (
                <Field
                  label={tm.form?.nfDate || "Data Emissão"}
                  value={
                    movement.nfDate
                      ? momentTimeZoneHour(movement.nfDate).format("DD/MM/YYYY")
                      : "-"
                  }
                />
              )}
              {movement.receiptDate && (
                <Field
                  label={tm.form?.receiptDate || "Data Recebimento"}
                  value={
                    movement.receiptDate
                      ? momentTimeZoneHour(movement.receiptDate).format(
                          "DD/MM/YYYY",
                        )
                      : "-"
                  }
                />
              )}
            </Row>

            {movement.itens &&
              movement.itens.length > 0 &&
              movement.itens.map((item) => (
                <div key={item.id}>
                  <Divider orientation="left">{t.itemInfo || "Item"}</Divider>
                  <Row>
                    <Field
                      label={tm.table?.item || "Item"}
                      value={item.stockItemName}
                    />
                    <Field
                      label={tm.table?.quantity || "Quantidade"}
                      value={
                        item.quantity != null
                          ? numberMask(item.quantity, false, null, null, 4, 2)
                          : "-"
                      }
                    />
                    <Field
                      label={tm.table?.measurementUnit || "Unidade de Medida"}
                      value={
                        item.measurementUnit != null
                          ? item.measurementUnit
                          : "-"
                      }
                    />
                    <Field
                      label={tm.table?.unitCost || "Custo Unitário"}
                      value={
                        item.quantity != null
                          ? numberMask(item.cost, true, null, null, 5, 5)
                          : "-"
                      }
                    />
                    <Field
                      label={tm.table?.totalCost || "Custo Total"}
                      value={
                        item.quantity != null && item.cost != null
                          ? numberMask(item.totalCost, true, null, null, 5, 5)
                          : "-"
                      }
                    />
                    <Field
                      label={tm.table?.batch || "Lote"}
                      value={item.batchName ? item.batchName : item.batchNumber}
                    />
                    {movement.nfCode && (
                      <Field
                        label={tm.table?.nf || "Ref. NF"}
                        value={movement.nfCode}
                      />
                    )}
                  </Row>
                </div>
              ))}

            {movement.observations && (
              <>
                <Divider orientation="left">
                  {t.observations || "Observações"}
                </Divider>
                <p>{movement.observations}</p>
              </>
            )}

            <Divider />
            <Row type="flex" justify="space-between" align="middle">
              <Col>
                <Button
                  icon="file-text"
                  disabled={!movement.invoiceId}
                  onClick={() => setDanfeVisible(true)}
                >
                  {t.viewNfe || "Ver NF-e"}
                </Button>
              </Col>
              <Col>
                <ButtonStandard
                  type="button"
                  buttonType="cancel"
                  disabled={isBlocked}
                  onClick={confirmDelete}
                >
                  {t.delete || "Excluir"}
                </ButtonStandard>
              </Col>
            </Row>

            <DanfeViewerModal
              visible={danfeVisible}
              invoiceId={movement.invoiceId}
              groupId={groupId}
              farmId={farmId}
              onClose={() => setDanfeVisible(false)}
            />
          </>
        )}
      </Spin>
    </Drawer>
  );
};

export default StockMovementDetailDrawer;
